import React from 'react';
import MainLayout from '../../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../../../backoffice/shell/backofficeSidebar';
import { useAuth } from '../../../../../context/AuthContext';
import { useTranslation } from 'react-i18next';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type AppParametersPayload = {
  configured: boolean;
  required: {
    paidRefundWindowHours: number;
    deliveredRefundWindowHours: number;
  };
};

export default function AppParametersPage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [configured, setConfigured] = React.useState(false);
  const [paidRefundWindowHours, setPaidRefundWindowHours] = React.useState(2);
  const [deliveredRefundWindowHours, setDeliveredRefundWindowHours] =
    React.useState(24);

  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init?: RequestInit) => {
      const nextHeaders = new Headers(init?.headers || undefined);
      nextHeaders.set('x-department-slug', '');
      return authorizedFetch(input, { ...(init || {}), headers: nextHeaders });
    },
    [authorizedFetch],
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/app-parameters`);
      const data = (await res.json()) as AppParametersPayload;
      if (!res.ok) {
        throw new Error(
          (data as any)?.message ||
            t('common.error_generic', { defaultValue: 'Something went wrong' }),
        );
      }
      setConfigured(Boolean(data.configured));
      setPaidRefundWindowHours(Number(data.required?.paidRefundWindowHours || 2));
      setDeliveredRefundWindowHours(
        Number(data.required?.deliveredRefundWindowHours || 24),
      );
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('common.error_generic', { defaultValue: 'Something went wrong' }),
      );
    } finally {
      setLoading(false);
    }
  }, [globalAuthorizedFetch, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = React.useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/settings/app-parameters`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paidRefundWindowHours: Math.max(
            1,
            Math.trunc(Number(paidRefundWindowHours || 0)),
          ),
          deliveredRefundWindowHours: Math.max(
            1,
            Math.trunc(Number(deliveredRefundWindowHours || 0)),
          ),
        }),
      });
      const data = (await res.json()) as AppParametersPayload & { ok?: boolean };
      if (!res.ok) {
        throw new Error(
          (data as any)?.message ||
            t('common.error_generic', { defaultValue: 'Something went wrong' }),
        );
      }
      setConfigured(Boolean(data.configured));
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('common.error_generic', { defaultValue: 'Something went wrong' }),
      );
    } finally {
      setSaving(false);
    }
  }, [
    deliveredRefundWindowHours,
    globalAuthorizedFetch,
    paidRefundWindowHours,
    t,
  ]);

  return (
    <MainLayout withSidebar sidebarItems={getBackofficeSidebarItems(t)} activePath="/workspace/app-parameters">
      <section className="space-y-4 pb-8">
        <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <h1 className="text-xl font-semibold text-slate-900">
            {t('backoffice.app_parameters_title', {
              defaultValue: 'Application parameters',
            })}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {t('backoffice.app_parameters_subtitle', {
              defaultValue:
                'Central initial setup. Other sidebar modules remain disabled until required parameters are configured.',
            })}
          </p>
          <div className="mt-2">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${
                configured
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-amber-100 text-amber-700'
              }`}
            >
              {configured
                ? t('backoffice.app_parameters_status_ready', {
                    defaultValue: 'Configured',
                  })
                : t('backoffice.app_parameters_status_missing', {
                    defaultValue: 'Configuration required',
                  })}
            </span>
          </div>
        </header>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          {loading ? (
            <p className="text-sm text-slate-600">
              {t('common.loading', { defaultValue: 'Loading...' })}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-600">
                    <th className="px-3 py-2 font-semibold">
                      {t('backoffice.app_parameters_col_key', { defaultValue: 'Parameter' })}
                    </th>
                    <th className="px-3 py-2 font-semibold">
                      {t('backoffice.app_parameters_col_value', { defaultValue: 'Value' })}
                    </th>
                    <th className="px-3 py-2 font-semibold">
                      {t('backoffice.app_parameters_col_desc', { defaultValue: 'Description' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="px-3 py-3 font-medium text-slate-900">
                      Paid refund h
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        min={1}
                        max={720}
                        value={paidRefundWindowHours}
                        onChange={(e) =>
                          setPaidRefundWindowHours(
                            Math.max(1, Math.trunc(Number(e.target.value || 1))),
                          )
                        }
                        className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                      />
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      Maximum hours after payment where refund request is allowed.
                    </td>
                  </tr>
                  <tr>
                    <td className="px-3 py-3 font-medium text-slate-900">
                      Delivered refund h
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        min={1}
                        max={720}
                        value={deliveredRefundWindowHours}
                        onChange={(e) =>
                          setDeliveredRefundWindowHours(
                            Math.max(1, Math.trunc(Number(e.target.value || 1))),
                          )
                        }
                        className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                      />
                    </td>
                    <td className="px-3 py-3 text-slate-600">
                      Maximum hours after delivery where refund request is allowed.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {error ? (
            <p className="mt-3 text-sm text-rose-600">{error}</p>
          ) : null}

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => {
                void save();
              }}
              disabled={loading || saving}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving
                ? t('common.saving', { defaultValue: 'Saving...' })
                : t('common.save', { defaultValue: 'Save' })}
            </button>
          </div>
        </div>
      </section>
    </MainLayout>
  );
}
